import { WirePaymentsService } from './wirePayments';
import { WIRE_PAYMENT_PROCESSING_STATUSES } from '../components/common/WirePayments/utils/useWirePayments';

interface WirePaymentBuilderOptions {
  page?: number;
  limit?: number;
  createdAtGte?: string;
  createdAtLte?: string;
  processedDateGte?: string;
  processedDateLte?: string;
  depositedDateGte?: string;
  depositedDateLte?: string;
  rejectedDateGte?: string;
  rejectedDateLte?: string;
  prismStatus?: string[];
  contractCare?: boolean;
  status?: string[];
  processingStatus?: WIRE_PAYMENT_PROCESSING_STATUSES[];
  search?: string;
  sort?: string;
}

export class WirePaymentBuilder {
  private page?: number;
  private limit?: number;
  private createdAtGte?: string;
  private createdAtLte?: string;
  private processedDateGte?: string;
  private processedDateLte?: string;
  private depositedDateGte?: string;
  private depositedDateLte?: string;
  private rejectedDateGte?: string;
  private rejectedDateLte?: string;
  private prismStatus?: string[];
  private contractCare?: boolean;
  private status?: string[];
  private processingStatus?: WIRE_PAYMENT_PROCESSING_STATUSES[];
  private search?: string;
  private sort: string = '-depositedDate';
  public hasMore: boolean;

  private constructor(options: WirePaymentBuilderOptions) {
    this.page = options.page;
    this.limit = options.limit;
    this.createdAtGte = options.createdAtGte;
    this.createdAtLte = options.createdAtLte;
    this.processedDateGte = options.processedDateGte;
    this.processedDateLte = options.processedDateLte;
    this.depositedDateGte = options.depositedDateGte;
    this.depositedDateLte = options.depositedDateLte;
    this.rejectedDateGte = options.rejectedDateGte;
    this.rejectedDateLte = options.rejectedDateLte;
    this.prismStatus = options.prismStatus;
    this.contractCare = options.contractCare;
    this.status = options.status;
    this.processingStatus = options.processingStatus;
    this.search = options.search;
    this.sort = options.sort || '-depositedDate';
    this.hasMore = true;
  }

  static builder = class {
    private page?: number;
    private limit?: number;
    private createdAtGte?: string;
    private createdAtLte?: string;
    private processedDateGte?: string;
    private processedDateLte?: string;
    private depositedDateGte?: string;
    private depositedDateLte?: string;
    private rejectedDateGte?: string;
    private rejectedDateLte?: string;
    private prismStatus?: string[];
    private contractCare?: boolean;
    private status?: string[];
    private processingStatus?: WIRE_PAYMENT_PROCESSING_STATUSES[];
    private search?: string;
    private sort: string = '-depositedDate';

    setPage(page: number): this {
      this.page = page;
      return this;
    }

    setLimit(limit: number): this {
      this.limit = limit;
      return this;
    }

    setCreatedAtGte(date: string): this {
      this.createdAtGte = date;
      return this;
    }

    setCreatedAtLte(date: string): this {
      this.createdAtLte = date;
      return this;
    }

    setProcessedDateGte(date: string): this {
      this.processedDateGte = date;
      return this;
    }

    setProcessedDateLte(date: string): this {
      this.processedDateLte = date;
      return this;
    }

    setDepositedDateGte(date: string): this {
      this.depositedDateGte = date;
      return this;
    }

    setDepositedDateLte(date: string): this {
      this.depositedDateLte = date;
      return this;
    }

    setRejectedDateGte(date: string): this {
      this.rejectedDateGte = date;
      return this;
    }

    setRejectedDateLte(date: string): this {
      this.rejectedDateLte = date;
      return this;
    }

    setPrismStatus(statuses: string[]): this {
      this.prismStatus = statuses;
      return this;
    }

    setContractCare(flag: boolean): this {
      this.contractCare = flag;
      return this;
    }

    setStatus(status: string[]): this {
      this.status = status;
      return this;
    }

    setProcessingStatus(status: WIRE_PAYMENT_PROCESSING_STATUSES[]): this {
      this.processingStatus = status;
      return this;
    }

    setSearch(search: string): this {
      this.search = search;
      return this;
    }

    setSort(sort: string): this {
      this.sort = sort;
      return this;
    }

    build(): WirePaymentBuilder {
      return new WirePaymentBuilder({
        page: this.page,
        limit: this.limit,
        createdAtGte: this.createdAtGte,
        createdAtLte: this.createdAtLte,
        processedDateGte: this.processedDateGte,
        processedDateLte: this.processedDateLte,
        depositedDateGte: this.depositedDateGte,
        depositedDateLte: this.depositedDateLte,
        rejectedDateGte: this.rejectedDateGte,
        rejectedDateLte: this.rejectedDateLte,
        prismStatus: this.prismStatus,
        contractCare: this.contractCare,
        status: this.status,
        processingStatus: this.processingStatus,
        search: this.search,
        sort: this.sort,
      });
    }
  };

  async fetchNext(): Promise<any> {
    try {
      const queryParams = new URLSearchParams();

      if (this.page) queryParams.append('page', this.page.toString());
      if (this.limit) queryParams.append('limit', this.limit.toString());
      if (this.createdAtGte)
        queryParams.append('createdAtGte', this.createdAtGte);
      if (this.createdAtLte)
        queryParams.append('createdAtLte', this.createdAtLte);
      if (this.processedDateGte)
        queryParams.append('processedDateGte', this.processedDateGte);
      if (this.processedDateLte)
        queryParams.append('processedDateLte', this.processedDateLte);
      if (this.depositedDateGte)
        queryParams.append('depositedDateGte', this.depositedDateGte);
      if (this.depositedDateLte)
        queryParams.append('depositedDateLte', this.depositedDateLte);
      if (this.rejectedDateGte)
        queryParams.append('rejectedDateGte', this.rejectedDateGte);
      if (this.rejectedDateLte)
        queryParams.append('rejectedDateLte', this.rejectedDateLte);
      if (this.prismStatus?.length)
        queryParams.append('prismStatus', this.prismStatus.join(','));
      if (typeof this.contractCare === 'boolean')
        queryParams.append('contractCare', String(this.contractCare));
      if (this.status?.length)
        queryParams.append('status', this.status.join(','));
      if (this.processingStatus?.length)
        queryParams.append('processingStatus', this.processingStatus.join(','));
      if (this.search) queryParams.append('search', this.search);
      if (this.sort) queryParams.append('sort', this.sort);

      const response = await new WirePaymentsService().getWirePayments(
        queryParams.toString()
      );

      if (response.status === 200) {
        return {
          data: response.data.data,
          total: response.data.total,
          page: this.page,
          limit: this.limit,
        };
      }

      throw new Error('Failed to fetch wire payments');
    } catch (error) {
      console.error('Error fetching wire payments:', error);
      throw error;
    }
  }
}
